import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { confirmDialog } from "../../../utils/confirm";
import { Briefcase, Plus, Pencil, Trash2, Eye } from "lucide-react";
import Swal from "sweetalert2";
import Button from "../../../components/Button";
import AddBroker from "./AddBroker";
import BrokerDetailsModal from "./BrokerDetailsModal";

import PageLoader from "../../../components/PageLoader";
import {
  getBrokers as apiGetBrokers,
  createBroker as apiCreateBroker,
  updateBroker as apiUpdateBroker,
  deleteBroker as apiDeleteBroker,
} from "../../../api";

const AdminBrokers = () => {
  const [brokers, setBrokers] = useState([]);
  const [brokerDetails, setBrokerDetails] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  const [viewingBroker, setViewingBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const list = await apiGetBrokers();
      const safeList = Array.isArray(list) ? list : [];
      setBrokers(safeList);

      const detailsMap = {};
      safeList.forEach((b) => {
        if (!b || !b.name) return;
        detailsMap[b.name] = {
          // basic fields used in table
          rating: b.rating,
          activeClients: b.activeClients,
          brokerage: b.brokerage,
          features: b.features,
          description: b.description,
          // extended stats used by AddBroker edit form
          activeClientsTrend: b.activeClientsTrend,
          charges: b.charges,
          complaints: b.complaints,
          shareHolding: b.shareHolding,
          pros: b.pros,
          cons: b.cons,
          financials: b.financials,
        };
      });
      setBrokerDetails(detailsMap);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load brokers from API.",
      );
      setBrokers([]);
      setBrokerDetails({});
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (brokerPayload, detailsPayload, oldName) => {
    try {
      setError("");
      if (editingBroker && editingBroker.id) {
        await apiUpdateBroker(editingBroker.id, {
          ...brokerPayload,
          ...detailsPayload,
        });
      } else {
        await apiCreateBroker({
          ...brokerPayload,
          ...detailsPayload,
        });
      }
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Broker ${editingBroker ? "updated" : "added"} successfully`,
        confirmButtonColor: "#38915c",
      });
      await load(false);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to save broker";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#38915c",
      });
    }
    setModalOpen(false);
    setEditingBroker(null);
  };

  const handleEdit = (b) => {
    setEditingBroker(b);
    setModalOpen(true);
  };

  const handleView = (b) => {
    setViewingBroker(b);
    setViewModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete broker?",
      text: "This broker will be removed from the list.",
    });
    if (!confirmed) return;
    try {
      await apiDeleteBroker(id);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Broker deleted successfully",
        confirmButtonColor: "#38915c",
      });
      await load(false);
      if (editingBroker?.id === id) {
        setEditingBroker(null);
        setModalOpen(false);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to delete broker";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#38915c",
      });
    }
  };

  const openAddModal = () => {
    setEditingBroker(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingBroker(null);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewingBroker(null);
  };

  return (
    <div>
      {loading && <PageLoader />}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <Briefcase className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              Manage
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Brokers</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Stock brokers listed on the platform
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={openAddModal}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Broker
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <AddBroker
        open={modalOpen}
        onClose={closeModal}
        onSuccess={handleSave}
        editBroker={editingBroker}
        brokerDetails={brokerDetails}
      />

      <BrokerDetailsModal
        open={viewModalOpen}
        onClose={closeViewModal}
        broker={viewingBroker}
        details={viewingBroker ? brokerDetails[viewingBroker.name] : null}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Broker
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Rating
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Active clients
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Brokerage
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Description
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {brokers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No brokers yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                brokers.map((b) => {
                  const d = brokerDetails[b.name] || {};
                  return (
                    <tr
                      key={b.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xl" aria-hidden>
                            {b.logo || "📊"}
                          </span>
                          <span className="font-medium text-gray-800">
                            {b.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-0.5 text-amber-600 font-medium">
                          {d.rating != null ? Number(d.rating) : "—"} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {d.activeClients || "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {d.brokerage || "—"}
                      </td>
                      <td
                        className="py-3 px-4 text-gray-600 max-w-[200px] truncate"
                        title={d.description}
                      >
                        {d.description || "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Tooltip title="View Details">
                            <button
                              type="button"
                              onClick={() => handleView(b)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <button
                              type="button"
                              onClick={() => handleEdit(b)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <button
                              type="button"
                              onClick={() => handleDelete(b.id)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminBrokers;
